import { useState } from "react";
import { Check, Palette } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { PRESET_PALETTES } from "./presetPalettes";

interface PresetPalettePickerProps {
  onSelect: (primary: string, secondary: string) => void;
  currentPrimary?: string;
  currentSecondary?: string;
}

export const PresetPalettePicker = ({ onSelect, currentPrimary, currentSecondary }: PresetPalettePickerProps) => {
  const [open, setOpen] = useState(false);

  const isSelected = (primary: string, secondary: string) =>
    primary.toLowerCase() === currentPrimary?.toLowerCase() &&
    secondary.toLowerCase() === currentSecondary?.toLowerCase();

  if (!open) {
    return (
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)} className="text-xs gap-1.5">
        <Palette className="w-3.5 h-3.5" />
        Not a fan of these colors? Pick a different palette
      </Button>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-600">Or choose a preset brand palette</p>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)} className="text-xs h-6 px-2">
          Hide
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {PRESET_PALETTES.map((preset) => (
          <button
            key={preset.name}
            type="button"
            onClick={() => onSelect(preset.primary, preset.secondary)}
            className={cn(
              "relative group flex flex-col items-center gap-1 rounded-md border-2 p-2 transition-all",
              isSelected(preset.primary, preset.secondary)
                ? "border-blue-500 ring-2 ring-blue-200"
                : "border-gray-200 hover:border-gray-300 hover:scale-105",
            )}
            title={preset.name}
          >
            <div
              className="w-full h-8 rounded"
              style={{ background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})` }}
            />
            <span className="text-[11px] text-gray-600">{preset.name}</span>
            {isSelected(preset.primary, preset.secondary) && (
              <div className="absolute top-1 right-1 bg-blue-500 rounded-full p-0.5">
                <Check className="w-2.5 h-2.5 text-white" />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
